import moment from 'moment';

import type {
  ApprovedItem,
  AutomatedSkillPathsFilter,
  ProficiencyLevel,
  Recommendation,
  RecommendationReason,
  RecommendationReasonCode,
  SkillRef
} from '../types';
import {MAX_SKILLS_PER_STUDENT} from '../constants';
import type {AutomatedSkillPathsApi} from './automatedSkillPathsApi';
import {BUILDS_ON, BUILDS_TOWARD, FIXTURE_SKILLS, RELATES_TO, TESTED_SKILL_IDS} from './fixtures';
import {completedStudentsOf} from './mockRoster';

interface SkillResult {
  skillId: string;
  level: ProficiencyLevel;
}

/** How long `generateRecommendations` takes, so its pending state is visible. */
const GENERATE_DELAY_MS = 400;
const START_SPACING_DAYS = 3;
const DUE_AFTER_DAYS = 7;
const LEVELS: ProficiencyLevel[] = ['UNRANKED', 'BEGINNER', 'INTERMEDIATE', 'ADVANCED'];

const SKILLS_BY_ID: Record<string, SkillRef> = Object.fromEntries(
  FIXTURE_SKILLS.map((skill) => [skill.id, skill])
);

let recommendations: Recommendation[] = [];
let approved: ApprovedItem[] = [];
/** What approve recorded as removed, as the backend does. */
const removals: Recommendation[] = [];
let nextId = 1;

const wait = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });

/** FNV-1a scaled to [0, 1), so levels and fulfilled items hold across reloads. */
const unitHash = (key: string) =>
  ([...key].reduce((h, char) => Math.imul(h ^ char.charCodeAt(0), 16777619), 2166136261) >>> 0) /
  2 ** 32;

const isWeak = (level: ProficiencyLevel) => level === 'UNRANKED' || level === 'BEGINNER';

const isInScope = ({classroomIds}: AutomatedSkillPathsFilter, classroomId: string) =>
  !classroomIds || classroomIds.includes(classroomId);

const recommendedIn = (filter: AutomatedSkillPathsFilter) =>
  recommendations.filter(({classroom}) => isInScope(filter, classroom.id));

const reasonFrom = (code: RecommendationReasonCode, {skillId, level}: SkillResult) => ({
  code,
  sourceSkill: {id: skillId, name: SKILLS_BY_ID[skillId].name},
  sourceLevel: level
});

const fulfilledAtOf = ({id, startDate}: ApprovedItem) =>
  startDate && !moment(startDate).isAfter() && unitHash(id) < 0.25 ? startDate : null;

const reached = (
  edges: Record<string, string>,
  code: RecommendationReasonCode,
  from: SkillResult[]
) =>
  from
    .filter(({skillId}) => skillId in edges)
    .map((result) => ({skillId: edges[result.skillId], reason: reasonFrom(code, result)}));

/**
 * The student's path: weak, prerequisite, next, related; one entry per skill with every reason and
 * the student's own level when the diagnostics tested it.
 */
const pathFor = (studentId: string) => {
  const results = TESTED_SKILL_IDS.map((skillId) => ({
    skillId,
    level: LEVELS[Math.floor(unitHash(`${studentId}:${skillId}`) * LEVELS.length)]
  }));
  const levelBySkill = new Map(results.map(({skillId, level}) => [skillId, level]));
  const weak = results.filter(({level}) => isWeak(level));
  const proven = results.filter(({level}) => !isWeak(level));
  const reasonsBySkill = new Map<string, RecommendationReason[]>();
  [
    ...weak.map((result) => ({skillId: result.skillId, reason: reasonFrom('WEAK', result)})),
    ...reached(BUILDS_TOWARD, 'BUILDS_TOWARD', weak),
    ...reached(BUILDS_ON, 'BUILDS_ON', proven),
    ...reached(RELATES_TO, 'RELATES_TO', proven)
  ].forEach(({skillId, reason}) =>
    reasonsBySkill.set(skillId, [...(reasonsBySkill.get(skillId) ?? []), reason])
  );
  return [...reasonsBySkill].slice(0, MAX_SKILLS_PER_STUDENT).map(([skillId, reasons]) => ({
    skillId,
    reasons,
    ownLevel: levelBySkill.get(skillId) ?? null
  }));
};

export const mockApi: AutomatedSkillPathsApi = {
  generateRecommendations: async ({diagnosticIds}) => {
    const [{students, diagnostics}] = await Promise.all([
      completedStudentsOf(diagnosticIds),
      wait(GENERATE_DELAY_MS)
    ]);
    const generatedAt = moment().toISOString();
    const created = students.flatMap(({student, classroom}) =>
      pathFor(student.id).map(({skillId, reasons, ownLevel}) => ({
        id: `recommendation-${nextId++}`,
        student,
        classroom,
        skill: SKILLS_BY_ID[skillId],
        ownLevel,
        reasons,
        diagnostics,
        generatedAt
      }))
    );
    const studentIds = new Set(students.map(({student}) => student.id));
    recommendations = [
      ...recommendations.filter(({student}) => !studentIds.has(student.id)),
      ...created
    ];
    return {
      result: {
        status: 'COMPLETE',
        studentCount: students.length,
        recommendationCount: created.length
      },
      errors: []
    };
  },

  recommendedSkills: async (filter) => recommendedIn(filter),

  recommendedSkillsCount: async (filter) => recommendedIn(filter).length,

  approveRecommendations: async ({recommendationIds, dismissedIds}) => {
    const picked = recommendations.filter(({id}) => recommendationIds.includes(id));
    const approvedStudentIds = new Set(picked.map(({student}) => student.id));
    approved = approved.filter(
      (item) => !approvedStudentIds.has(item.student.id) || fulfilledAtOf(item) !== null
    );
    const today = moment().startOf('day');
    const created = picked.map(
      ({student, classroom, skill, ownLevel, reasons, diagnostics, generatedAt}, index) => {
        const order = picked.slice(0, index).filter((r) => r.student.id === student.id).length;
        const start = today.clone().add(order * START_SPACING_DAYS, 'days');
        return {
          id: `approved-${nextId++}`,
          student,
          classroom,
          skill,
          ownLevel,
          reasons,
          diagnostics,
          generatedAt,
          startDate: start.toISOString(),
          dueDate: start.clone().add(DUE_AFTER_DAYS, 'days').toISOString(),
          fulfilledAt: null
        };
      }
    );
    removals.push(...recommendations.filter(({id}) => dismissedIds.includes(id)));
    recommendations = recommendations.filter(
      ({id}) => !recommendationIds.includes(id) && !dismissedIds.includes(id)
    );
    approved = [...approved, ...created];
    return {result: {approvedCount: created.length}, errors: []};
  },

  approvedSkills: async (filter) =>
    approved
      .filter(({classroom}) => isInScope(filter, classroom.id))
      .map((item) => ({...item, fulfilledAt: fulfilledAtOf(item)}))
};
